/**
 * Freshness utility functions for position visualization.
 *
 * Uses timestamps to determine visual properties (opacity, saturation, color)
 * based on age of position data.
 *
 * A vehicle is ACTIVE if: V1 < 10s OR V4 < 20s (from FRESHNESS config)
 */

import { FRESHNESS } from '../config';
import type { DataSource } from '../types/api';

// Re-export color utilities for backwards compatibility
export {
  hexToRgb,
  getLuminance,
  getContrastingIconColor,
  desaturateColor,
} from './colors';

// =============================================================================
// TYPES
// =============================================================================

export type FreshnessState = 'fresh' | 'aging' | 'stale';

// =============================================================================
// THRESHOLDS (derived from FRESHNESS config)
// =============================================================================

export const FRESHNESS_THRESHOLDS = {
  /** GPS data is fresh if age < V1_ACTIVE_MS */
  GPS_FRESH_MS: FRESHNESS.V1_ACTIVE_MS,
  /** GPS data is aging if age >= V1_ACTIVE_MS but < 2x threshold */
  GPS_AGING_MS: FRESHNESS.V1_ACTIVE_MS * 2,
  /** V4 data is fresh if age < V4_ACTIVE_MS */
  V4_FRESH_MS: FRESHNESS.V4_ACTIVE_MS,
} as const;

// =============================================================================
// FRESHNESS STATE
// =============================================================================

/**
 * Get freshness state for GPS data based on age.
 *
 * @param ageMs - Age of GPS data in milliseconds, null if unavailable
 * @returns Freshness state
 */
export function getGpsFreshnessState(ageMs: number | null): FreshnessState {
  if (ageMs === null) return 'stale';
  if (ageMs < FRESHNESS_THRESHOLDS.GPS_FRESH_MS) return 'fresh';
  if (ageMs < FRESHNESS_THRESHOLDS.GPS_AGING_MS) return 'aging';
  return 'stale';
}

/**
 * Get freshness state for V4 estimated data based on age.
 *
 * @param ageMs - Age of V4 data in milliseconds, null if unavailable
 * @returns Freshness state
 */
export function getV4FreshnessState(ageMs: number | null): FreshnessState {
  if (ageMs === null) return 'stale';
  if (ageMs < FRESHNESS_THRESHOLDS.V4_FRESH_MS) return 'fresh';
  return 'stale';
}

// =============================================================================
// OPACITY FUNCTIONS
// =============================================================================

/**
 * Get opacity based on data source and freshness.
 *
 * GPS data gets higher opacity, estimated data gets lower opacity.
 *
 * @param dataSource - Primary data source
 * @param gpsAgeMs - Age of GPS data, null if unavailable
 * @param v4AgeMs - Age of V4 data, null if unavailable
 * @returns Opacity value (0.3 to 1.0)
 */
export function getVehicleOpacity(
  dataSource: DataSource,
  gpsAgeMs: number | null,
  v4AgeMs: number | null
): number {
  if (dataSource === 'v1-da-gps') {
    const state = getGpsFreshnessState(gpsAgeMs);
    switch (state) {
      case 'fresh': return 1.0;
      case 'aging': return 0.7;
      case 'stale': return 0.4;
    }
  }

  // V4 estimated
  const state = getV4FreshnessState(v4AgeMs);
  return state === 'fresh' ? 0.7 : 0.4;
}

// =============================================================================
// COLOR FUNCTIONS
// =============================================================================

/** Default grey color for stale/unknown data */
export const GREYSCALE_COLOR = '#888888';

/** Amber color for V4 override indication */
export const OVERRIDE_COLOR = '#f59e0b';

/**
 * Get display color based on data source and freshness.
 *
 * @param originalColor - Line's original background color (hex)
 * @param dataSource - Primary data source
 * @param gpsAgeMs - Age of GPS data, null if unavailable
 * @returns Color to display (hex)
 */
export function getVehicleColor(
  originalColor: string | null,
  dataSource: DataSource,
  gpsAgeMs: number | null
): string {
  const baseColor = originalColor || '#2563eb';

  if (dataSource === 'v1-da-gps') {
    const state = getGpsFreshnessState(gpsAgeMs);
    return state === 'stale' ? GREYSCALE_COLOR : baseColor;
  }

  // V4 estimated - show slightly desaturated
  return baseColor;
}

// =============================================================================
// DESCRIPTION FUNCTIONS
// =============================================================================

/**
 * Get human-readable description for a vehicle's position state.
 *
 * @param dataSource - Primary data source
 * @param gpsAgeMs - Age of GPS data, null if unavailable
 * @param v4AgeMs - Age of V4 data, null if unavailable
 * @returns Human-readable description
 */
export function getPositionDescription(
  dataSource: DataSource,
  gpsAgeMs: number | null,
  v4AgeMs: number | null
): string {
  if (dataSource === 'v1-da-gps') {
    const state = getGpsFreshnessState(gpsAgeMs);
    switch (state) {
      case 'fresh': return 'Live GPS tracking';
      case 'aging': return 'GPS data slightly delayed';
      case 'stale': return 'GPS data aging';
    }
  }

  const state = getV4FreshnessState(v4AgeMs);
  return state === 'fresh' ? 'Using estimated position' : 'Position data outdated';
}

// =============================================================================
// FORMATTING FUNCTIONS
// =============================================================================

/**
 * Format data age for display.
 *
 * @param lastUpdateTime - Timestamp of last position update
 * @param currentTime - Current timestamp (defaults to Date.now())
 * @returns Formatted string like "5s ago" or "2m ago"
 */
export function formatDataAge(
  lastUpdateTime: number,
  currentTime: number = Date.now()
): string {
  const ageMs = currentTime - lastUpdateTime;
  const seconds = Math.floor(ageMs / 1000);

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ago`;
}
